import type { Data, HolidayRow, SchedulePreviewCardProps } from './types'
import type { Group } from '../types'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'

import { ExclamationCircleOutlined, FileTextOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { Calendar, Card, Spin, Tabs } from 'antd'
import classNames from 'classnames'
import { Fragment, useMemo, useState } from 'react'

import $api from '../../../../API'
import { NextRun } from '../next-run'

export function SchedulePreviewCard({ onGroupSelect }: SchedulePreviewCardProps): JSX.Element {
  const [value, setValue] = useState(dayjs())

  const holidaysQuery = useQuery({
    queryKey: ['holidays'],

    async queryFn() {
      const response = await $api.get<HolidayRow[]>('/holidays')

      return response.data
    }
  })

  const filesQuery = useQuery<unknown, unknown, Data, string[]>({
    queryKey: ['schedule-preview', value.format('YYYY-MM-DD')],

    async queryFn(context) {
      const [, date] = context.queryKey

      const response = await $api.get<Data>('/groups/schedule-preview', {
        params: {
          date
        }
      })

      return response.data
    },

    keepPreviousData: true
  })

  const { badge, subtext } = useMemo(() => {
    if (filesQuery.isLoading && !filesQuery.data) {
      return { badge: '', subtext: '' }
    }

    const chicagoNow = dayjs().tz('America/Chicago')
    const todayStr = chicagoNow.format('YYYY-MM-DD')
    const selectedStr = value.format('YYYY-MM-DD')

    const isToday = selectedStr === todayStr
    const isFuture = selectedStr > todayStr
    const isPast = selectedStr < todayStr

    if (!filesQuery.data || filesQuery.data.files.length === 0) {
      return { badge: 'No schedule available', subtext: '' }
    }

    const { lastUpdatedAt } = filesQuery.data

    let badgeLabel = ''
    let helperText = ''

    if (isPast) {
      badgeLabel = 'Historical'
      helperText = 'Sent schedule for this day'
    } else if (isToday) {
      const isAfterCutoff = chicagoNow.hour() >= 3

      if (isAfterCutoff) {
        badgeLabel = 'Historical'
        if (lastUpdatedAt) {
          helperText = `Last updated: ${dayjs(lastUpdatedAt).tz('America/Chicago').format('HH:mm')}`
        }
      } else {
        badgeLabel = 'Preview'
        helperText = 'Final schedule will appear after 03:00 AM CT'
      }
    } else if (isFuture) {
      badgeLabel = 'Preview'
      helperText = 'Based on current config'
    }

    return { badge: badgeLabel, subtext: helperText }
  }, [filesQuery.data, filesQuery.isLoading, value])

  const expiringGroupsQuery = useQuery({
    queryKey: ['expiring-groups', new Date().toISOString().slice(0, 10)],

    async queryFn({ queryKey }): Promise<Group[]> {
      const [, date] = queryKey

      const response = await $api.get<Group[]>('/groups/expiring', {
        params: {
          date,
          days: 3
        }
      })

      return response.data
    }
  })

  return (
    <Card className='w-full rounded-xl mt-4'>
      <div className='flex gap-6'>
        <div className='flex flex-col flex-grow'>
          <div className='flex items-baseline gap-4 mb-4'>
            <h3 className='text-lg'>{badge}</h3>
            <span className='text-sm text-gray-400'>{subtext}</span>
          </div>

          <Spin
            spinning={filesQuery.isLoading || filesQuery.isFetching}
            wrapperClassName='h-full min-h-[200px]'
          >
            {filesQuery.data?.files.length === 0 ? (
              <div className='flex items-center justify-center h-full text-gray-400 min-h-[200px]'>
                No schedule available
              </div>
            ) : (
              <Tabs
                tabPosition='top'
                size='small'
                className='h-full'
                items={
                  filesQuery.data?.files.map(file => ({
                    label: (
                      <div className='flex items-center'>
                        <FileTextOutlined className='!mr-2 text-gray-600 [.ant-tabs-tab-active_&]:text-blue-500' />
                        <span className=''>{file.name}</span>
                      </div>
                    ),
                    key: file.name,
                    children: (
                      <pre className='h-full max-h-72 overflow-auto max-w-[54rem]'>
                        {file.contents}
                      </pre>
                    )
                  })) ?? []
                }
              />
            )}
          </Spin>
        </div>

        <div className='flex flex-col w-80 flex-shrink-0'>
          <div className='py-2 border border-gray-100 rounded-lg shadow-sm'>
            <Calendar
              value={value}
              onSelect={setValue}
              fullscreen={false}
              dateFullCellRender={date => {
                const holidays =
                  holidaysQuery.data?.filter(
                    (holiday: { date: string | number | Dayjs | Date | null | undefined }) =>
                      date.isSame(holiday.date, 'day')
                  ) ?? []
                const hasHolidays = holidays.length > 0

                return (
                  <div
                    title={
                      holidays
                        .map((holiday: { description: unknown }) => holiday.description)
                        .join(', ') || undefined
                    }
                    className={classNames('ant-picker-cell-inner ant-picker-calendar-date', {
                      'is-holiday': hasHolidays,
                      'is-weekend': !hasHolidays && (date.day() === 0 || date.day() === 6)
                    })}
                  >
                    <div
                      className={classNames('ant-picker-calendar-date-value', {
                        'text-red-400 font-semibold': hasHolidays,
                        'text-gray-300': !hasHolidays && (date.day() === 0 || date.day() === 6)
                      })}
                    >
                      {date.format('D')}
                    </div>
                  </div>
                )
              }}
            />
          </div>
        </div>
      </div>

      <NextRun
        value={filesQuery.data?.nextRunAt ? new Date(filesQuery.data.nextRunAt) : undefined}
      />

      {Array.isArray(expiringGroupsQuery.data) && expiringGroupsQuery.data.length > 0 && (
        <div className='rounded-lg bg-blue-50 flex justify-start items-baseline py-1.5 px-5 mt-4 -mb-5 -mx-5 //w-[calc(100%_+_2rem)]'>
          <ExclamationCircleOutlined className='translate-y-0.5 mr-1.5' />

          <p>
            Groups about to expire:{' '}
            {expiringGroupsQuery.data.map((group, i, groups) => (
              <Fragment key={group.id}>
                <button
                  type='button'
                  className='text-blue-500 hover:text-blue-400'
                  onClick={() => {
                    onGroupSelect(group)
                  }}
                >
                  {group.number}
                </button>

                {i < groups.length - 1 && ', '}
              </Fragment>
            ))}
          </p>
        </div>
      )}
    </Card>
  )
}
